import { makeStyles } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import Loading from "../../../../common-components/Loading";
import { getPMRProgramPerformanceSlideAPI } from "../../../../api/reports-api";
import {
  usePlByProgramStyles,
  PlChartPanel,
} from "../pl-by-program/plByProgramTheme";
import { useLuTableSortFilter } from "../labor-utilization/tableSortFilterUtils";
import { WaterfallThLabel } from "../waterfall-eac/waterfallTableUtils";
import { ctFBtnSt, FilterIcon } from "../scenario-yoy/yoyTableShared";

import {
  TH_BG,
  TH_BG_BAND,
} from "../../reportTableConfig";

const NAV = "#0D2137";
const NAV2 = "#1E3A5F";
const HEADER_BG = TH_BG_BAND;
const PAGE_BG = "transparent";
const CARD_BG = "#FFFFFF";
const BORDER = "#E5E7EB";
const FONT = '"Roboto", "Helvetica", "Arial", sans-serif';
const REV_C = "#0D2137";
const GP_C = "#ED7D31";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_KEYS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/* Sort/filter columns for the monthly grid — clicking a month (or Total) header
   sorts the metric rows by that column's value; the funnel icon filters rows
   by that column's value (same engine as Labor Utilization's tables). */
const MONTH_COLS = [
  ...MONTH_KEYS.map((k, i) => ({ key: k, label: MONTHS[i] })),
  { key: "total", label: "Total" },
];

function buildDataRow(lineItem, items) {
  const src = items[lineItem.key] || {};
  const isPct = !!lineItem.isPct;
  const row = {
    label: lineItem.label,
    isPct,
    isSummary: lineItem.type === "subtotal" || lineItem.type === "metric",
  };
  const scale = (v) => (isPct && v != null ? v * 100 : v);
  MONTH_KEYS.forEach((k) => { row[k] = scale(src[k]); });
  row.total = scale(src.total);
  return row;
}

function buildHoursRow(label, obj) {
  const row = { label, isPct: false, isSummary: false, isHours: true };
  MONTH_KEYS.forEach((k) => { row[k] = obj?.[k]; });
  row.total = obj?.total;
  return row;
}

/** Reshape the flat PMR API response into the filter/summary/grid shape this page renders. */
function mapProgramPerformanceSlide(d) {
  const items = d.items || {};
  const revenueRow = items.contractRevenue || {};
  const gmRow = items.grossMargin || {};
  const toM = (v) => (v == null ? 0 : Math.round((v / 1e6) * 10) / 10);

  return {
    filters: [
      { label: "Project #",            value: d.projectNumber ?? "All" },
      { label: "Project Name",         value: d.projectName ?? "All" },
      { label: "Customer Name",        value: d.customerName ?? "All" },
      { label: "POP Start Date",       value: d.popStartDate ?? "All" },
      { label: "POP End Date",         value: d.popEndDate ?? "All" },
      { label: "Contract Type",        value: d.contractType ?? "All" },
      { label: "Project Manager Name", value: d.projectManagerName ?? "All" },
    ],
    projectValueRows: [
      { label: "Project Value Cost",         value: d.projectValueCost,  type: d.projectValueCost == null ? "na" : "currency" },
      { label: "Project Value Total Amount", value: d.projectTotalValue, type: "currency" },
    ],
    fundingSummaryRows: [
      { label: "Project Cost Funding",  value: d.projectCostFunding,  type: d.projectCostFunding == null ? "na" : "currency" },
      { label: "Project Fee Funding",   value: d.projectFeeFunding,   type: d.projectFeeFunding == null ? "na" : "currency" },
      { label: "Total Project Funding", value: d.projectTotalFunding, type: "currency" },
    ],
    totalCeilingValue: d.totalCeilingValue ?? 0,
    year: d.fiscalYear,
    workingDays: MONTH_KEYS.map((k) => d.businessDaysByMonth?.[k] ?? 0),
    actualsMonthCount: d.actualThroughMonth ?? 0,
    monthKeys: MONTH_KEYS,
    monthlyRows: [
      ...(d.lineItems || []).map((li) => buildDataRow(li, items)),
      buildHoursRow("Total Project Hours", d.totalProjectHours),
      buildHoursRow("IB Employee Hours", d.ibEmployeeHours),
      buildHoursRow("Subk Hours", d.subkHours),
    ],
    charts: {
      revenueM: MONTH_KEYS.map((k) => toM(revenueRow[k])),
      grossProfitM: MONTH_KEYS.map((k) => toM(gmRow[k])),
    },
  };
}

function fmtSummaryValue(row) {
  if (row.type === "na" || row.value == null) return "";
  if (row.type === "currency") {
    const n = Number(row.value);
    const abs = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return n < 0 ? `($${abs})` : `$${abs}`;
  }
  return String(row.value);
}

function fmtCellValue(row, key) {
  const v = row[key];
  if (v == null || v === "") return "";
  if (row.isPct) return `${Number(v).toFixed(1)}%`;
  if (row.isHours) return Number(v).toLocaleString("en-US");
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return n < 0 ? `($${abs})` : `$${abs}`;
}

function buildLineOption(dataM, yAxisName, color) {
  return {
    grid: { top: 40, right: 24, bottom: 36, left: 56 },
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const p = params[0];
        return `${p.name}: ${p.value}M`;
      },
    },
    xAxis: {
      type: "category",
      data: MONTHS,
      name: "Month",
      nameLocation: "middle",
      nameGap: 22,
      nameTextStyle: { fontSize: 11, color: "#64748B", fontFamily: FONT },
      axisLabel: { fontSize: 10, color: "#64748B", fontFamily: FONT },
      axisLine: { lineStyle: { color: "#CBD5E1" } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value",
      name: yAxisName,
      nameTextStyle: { fontSize: 11, color: "#64748B", fontFamily: FONT },
      axisLabel: {
        formatter: (v) => `${v}M`,
        fontSize: 10,
        color: "#64748B",
        fontFamily: FONT,
      },
      splitLine: { lineStyle: { type: "dashed", color: "#E9EDF3" } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: "line",
        data: dataM,
        smooth: false,
        symbol: "circle",
        symbolSize: 7,
        lineStyle: { width: 2, color },
        itemStyle: { color },
        label: {
          show: true,
          formatter: (p) => `${p.value}M`,
          position: "top",
          distance: 8,
          fontSize: 11,
          fontWeight: 700,
          color: "#1E293B",
          fontFamily: FONT,
          backgroundColor: "rgba(255,255,255,0.85)",
          borderRadius: 3,
          padding: [2, 4],
        },
      },
    ],
  };
}

function SummaryTable({ title, rows, classes }) {
  return (
    <div className={classes.summaryTable}>
      <table className={classes.summaryTableInner}>
        <thead>
          <tr>
            <th className={classes.summaryThLabel}>{title}</th>
            <th className={classes.summaryThValue}>Value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.label} className={i % 2 === 1 ? classes.summaryRowAlt : ""}>
              <td className={classes.summaryTdLabel}>{row.label}</td>
              <td className={classes.summaryTdValue}>{fmtSummaryValue(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FilterPanel({ rows, classes }) {
  return (
    <div className={classes.filterPanel}>
      {rows.map((row, i) => (
        <div
          key={row.label}
          className={`${classes.filterRow} ${i % 2 === 1 ? classes.filterRowAlt : ""}`}
        >
          <span className={classes.filterLabel}>{row.label}</span>
          <span className={classes.filterValue}>{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function MonthlyGrid({ data, classes }) {
  const { year, workingDays, actualsMonthCount, monthKeys, monthlyRows } = data;
  const actualsEnd = actualsMonthCount;

  const {
    sortKeys,
    sortedRows,
    openFilterKey,
    onThClick,
    getSortColumnLabel,
    hasFilterValue,
    toggleFilter,
    filterBtnRefs,
    renderFilterPanel,
  } = useLuTableSortFilter(MONTH_COLS, monthlyRows);

  const sortFilterThContent = (colKey, labelColor) => {
    const active = openFilterKey === colKey || hasFilterValue(colKey);
    return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
      <WaterfallThLabel
        label={MONTH_COLS.find((c) => c.key === colKey)?.label}
        colKey={colKey}
        sortKeys={sortKeys}
        getLabel={getSortColumnLabel}
        highlight={openFilterKey === colKey}
        align="center"
      />
      <button
        ref={(el) => { filterBtnRefs.current[colKey] = el; }}
        data-filter-btn
        type="button"
        style={active ? ctFBtnSt(true) : { ...ctFBtnSt(false), color: labelColor }}
        onClick={(e) => { e.stopPropagation(); toggleFilter(colKey, e.currentTarget); }}
        title="Filter"
      >
        <FilterIcon />
      </button>
    </div>
    );
  };

  return (
    <div className={classes.tableWrap}>
      <table className={classes.gridTable}>
        <thead>
          <tr>
            <th
              className={classes.thLabel}
              rowSpan={4}
              style={{ verticalAlign: "middle" }}
            >
              Month
            </th>
            <th className={classes.thYear} colSpan={12} style={{ textAlign: "center" }}>
              {year}
            </th>
            <th
              className={classes.thTotal}
              rowSpan={4}
              style={{ verticalAlign: "middle", textAlign: "center", cursor: "pointer" }}
              onClick={onThClick("total")}
            >
              {sortFilterThContent("total", "#F59E0B")}
            </th>
          </tr>
          <tr>
            <th className={classes.thBand} colSpan={actualsEnd}>
              Actuals to Date
            </th>
            <th className={classes.thBand} colSpan={12 - actualsEnd}>
              Estimate to Complete (ETC)
            </th>
          </tr>
          <tr>
            {MONTHS.map((m, i) => {
              const key = MONTH_KEYS[i];
              return (
                <th
                  key={m}
                  className={i < actualsEnd ? classes.thActual : classes.thForecast}
                  style={{ cursor: "pointer" }}
                  onClick={onThClick(key)}
                >
                  {sortFilterThContent(key, i < actualsEnd ? "rgba(255,255,255,.75)" : "rgba(255,255,255,.55)")}
                </th>
              );
            })}
          </tr>
          <tr>
            {workingDays.map((d, i) => (
              <th
                key={MONTHS[i]}
                className={i < actualsEnd ? classes.thSubActual : classes.thSubForecast}
              >
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, ri) => {
            const alt = !row.isSummary && ri % 2 === 1;
            const cellCls = row.isSummary
              ? classes.summaryDataRowCell
              : alt
                ? classes.tdActualAlt
                : "";
            const labelCls = row.isSummary
              ? classes.summaryDataRowLabel
              : classes.tdLabel;
            const totalCls = row.isSummary
              ? classes.summaryDataRowTotal
              : classes.tdTotal;
            return (
              <tr key={row.label}>
                <td className={labelCls}>{row.label}</td>
                {monthKeys.map((key, mi) => (
                  <td
                    key={key}
                    className={[
                      mi < actualsEnd ? classes.tdActual : classes.tdForecast,
                      cellCls,
                    ].join(" ")}
                  >
                    {fmtCellValue(row, key)}
                  </td>
                ))}
                <td className={totalCls}>{fmtCellValue(row, "total")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {renderFilterPanel()}
    </div>
  );
}

const useStyles = makeStyles(() => ({
  root: {
    background: PAGE_BG,
    minHeight: "100%",
    fontFamily: FONT,
    fontSize: "13px",
    color: "#334155",
    paddingBottom: "16px",
  },

  pageHeader: {
    background: `linear-gradient(130deg, ${NAV} 0%, ${NAV2} 100%)`,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 24px",
    marginBottom: "16px",
    boxShadow: "0 2px 12px rgba(13,33,55,0.3)",
  },
  headerTitleText: { fontSize: "16px", fontWeight: 700, letterSpacing: "0.3px" },
  headerBadge: {
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: "20px",
    padding: "2px 10px",
    fontSize: "11px",
    fontWeight: 600,
    color: "rgba(255,255,255,0.9)",
    marginLeft: "10px",
  },
  headerDate: {
    fontSize: "12px",
    fontWeight: 500,
    color: "rgba(255,255,255,0.75)",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: "8px",
    padding: "4px 12px",
  },

  contentCard: {
    background: CARD_BG,
    margin: "0 16px 16px",
    borderRadius: "12px",
    boxShadow: "0 1px 8px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },

  topRow: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 1fr) minmax(280px, 1.2fr) minmax(200px, 0.8fr)",
    gap: "20px",
    padding: "20px",
    alignItems: "start",
    "@media (max-width: 1100px)": {
      gridTemplateColumns: "1fr",
    },
  },

  filterPanel: {
    border: `1px solid ${BORDER}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  filterRow: {
    display: "flex",
    alignItems: "stretch",
    minHeight: "34px",
    borderBottom: `1px solid ${BORDER}`,
    background: CARD_BG,
    "&:last-child": { borderBottom: "none" },
  },
  filterRowAlt: { background: "#F8FAFC" },
  filterLabel: {
    flex: "0 0 52%",
    padding: "6px 10px",
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
    color: "#334155",
    borderRight: `1px solid ${BORDER}`,
  },
  filterValue: {
    flex: 1,
    padding: "6px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    fontWeight: 600,
    fontSize: "12px",
    color: NAV,
  },

  summaryStack: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  summaryTable: {
    border: `1px solid ${BORDER}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  summaryTableInner: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "12px",
  },
  summaryThLabel: {
    background: TH_BG_BAND,
    color: "rgba(255,255,255,.7)",
    padding: "9px 12px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "left",
    border: "none",
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  summaryThValue: {
    background: TH_BG,
    color: "rgba(255,255,255,.7)",
    padding: "9px 12px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "right",
    width: "45%",
    border: "none",
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  summaryTdLabel: {
    padding: "7px 12px",
    borderBottom: `1px solid ${BORDER}`,
    borderRight: `1px solid ${BORDER}`,
    color: "#334155",
  },
  summaryTdValue: {
    padding: "7px 12px",
    borderBottom: `1px solid ${BORDER}`,
    textAlign: "right",
    fontWeight: 600,
    color: NAV,
    fontVariantNumeric: "tabular-nums",
  },
  summaryRowAlt: {
    "& td": { background: "#F8FAFC" },
  },

  ceilingBlock: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "12px 8px",
    minHeight: "120px",
  },
  ceilingText: {
    fontSize: "15px",
    fontWeight: 700,
    color: NAV,
    textAlign: "center",
    lineHeight: 1.4,
    fontVariantNumeric: "tabular-nums",
  },

  sectionDivider: {
    height: "1px",
    background: BORDER,
    margin: "0 20px",
  },

  tableWrap: {
    overflowX: "auto",
    padding: "0 20px 20px",
  },
  gridTable: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontFamily: FONT,
    fontSize: 12,
    tableLayout: "auto",
  },

  /* ── header cells (WaterfallEAC style) ── */
  thLabel: {
    background: TH_BG_BAND,
    color: "rgba(255,255,255,.7)",
    padding: "0 14px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "left",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 155,
    position: "sticky",
    left: 0,
    zIndex: 5,
    boxSizing: "border-box",
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thYear: {
    background: TH_BG_BAND,
    color: "#fff",
    padding: "8px 10px",
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textAlign: "center",
    border: "none",
    whiteSpace: "nowrap",
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thBand: {
    background: TH_BG,
    color: "rgba(255,255,255,.85)",
    padding: "7px 8px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textAlign: "center",
    border: "none",
    whiteSpace: "nowrap",
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },
  thActual: {
    background: TH_BG,
    color: "rgba(255,255,255,.75)",
    padding: "6px 8px",
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: "0.04em",
    textAlign: "center",
    border: "none",
    minWidth: 90,
    whiteSpace: "nowrap",
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },
  thForecast: {
    background: TH_BG,
    color: "rgba(255,255,255,.55)",
    padding: "6px 8px",
    fontWeight: 600,
    fontSize: 10,
    textAlign: "center",
    border: "none",
    minWidth: 90,
    whiteSpace: "nowrap",
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },
  thTotal: {
    background: TH_BG,
    color: "#F59E0B",
    padding: "6px 10px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    textAlign: "right",
    border: "none",
    minWidth: 120,
    whiteSpace: "nowrap",
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },
  thSubActual: {
    background: TH_BG,
    color: "rgba(255,255,255,.5)",
    padding: "4px 6px",
    fontSize: 9,
    fontWeight: 500,
    textAlign: "center",
    border: "none",
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thSubForecast: {
    background: TH_BG,
    color: "rgba(255,255,255,.35)",
    padding: "4px 6px",
    fontSize: 9,
    fontWeight: 500,
    textAlign: "center",
    border: "none",
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },

  /* ── data cells ── */
  tdLabel: {
    padding: "7px 14px",
    fontWeight: 400,
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    background: "#F8FAFC",
    position: "sticky",
    left: 0,
    zIndex: 2,
    boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
  },
  tdActual: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#334155",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    background: "#fff",
    fontVariantNumeric: "tabular-nums",
  },
  tdForecast: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#64748B",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    background: "#FAFBFD",
    fontVariantNumeric: "tabular-nums",
  },
  tdTotal: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#F59E0B",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    background: "rgba(245,158,11,0.04)",
    fontVariantNumeric: "tabular-nums",
  },
  tdActualAlt: { background: "#F7FAFC !important" },
  summaryDataRowCell: {
    background: "#F0FFF4 !important",
    fontWeight: 700,
    color: "#111827 !important",
    borderBottom: "1px solid #CBD5E1",
  },
  summaryDataRowLabel: {
    padding: "7px 14px",
    fontWeight: 700,
    color: "#111827",
    whiteSpace: "nowrap",
    fontSize: 12,
    borderBottom: "1px solid #CBD5E1",
    background: "#F0FFF4",
    position: "sticky",
    left: 0,
    zIndex: 2,
    boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
  },
  summaryDataRowTotal: {
    padding: "7px 10px",
    textAlign: "right",
    fontWeight: 700,
    color: "#111827",
    fontSize: 12,
    borderBottom: "1px solid #CBD5E1",
    background: "rgba(245,158,11,0.06)",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },

  chartsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    padding: "0 20px 24px",
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  },
  errorText: { padding: "24px", color: "#DC2626", fontSize: 13, fontFamily: FONT },
}));

export default function ProgramPerformanceSlide({ filters = {}, onMeta }) {
  const classes   = useStyles();
  const plClasses = usePlByProgramStyles();
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [d,       setD]       = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPMRProgramPerformanceSlideAPI(filters)
      .then((res) => {
        if (cancelled) return;
        const raw = res?.data;
        if (raw) {
          setD(mapProgramPerformanceSlide(raw));
          onMeta?.({ actualThroughMonth: raw.actualThroughMonth, fiscalYear: raw.fiscalYear });
        } else {
          setError("Failed to load data.");
        }
      })
      .catch(() => { if (!cancelled) setError("Failed to load data."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Loading />;
  if (error || !d) return <div className={classes.errorText}>{error || "No data available."}</div>;

  const ceilingFmt = Number(d.totalCeilingValue).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className={classes.root}>
      <div className={classes.contentCard}>
        <div className={classes.topRow}>
          <FilterPanel rows={d.filters} classes={classes} />
          <div className={classes.summaryStack}>
            <SummaryTable
              title="CURRENT PROJECT VALUE"
              rows={d.projectValueRows}
              classes={classes}
            />
            <SummaryTable
              title="FUNDING"
              rows={d.fundingSummaryRows}
              classes={classes}
            />
          </div>
          <div className={classes.ceilingBlock}>
            <p className={classes.ceilingText}>
              TOTAL CEILING VALUE: {ceilingFmt}
            </p>
          </div>
        </div>

        <div className={classes.sectionDivider} />
        <MonthlyGrid data={d} classes={classes} />

        <div className={classes.chartsRow}>
          <PlChartPanel
            title="Revenue"
            option={buildLineOption(d.charts.revenueM, "Revenue", REV_C)}
            classes={plClasses}
            panelClassName={plClasses.chartPanelLg}
          />
          <PlChartPanel
            title="Gross Profit"
            option={buildLineOption(d.charts.grossProfitM, "Gross Profits", GP_C)}
            classes={plClasses}
            panelClassName={plClasses.chartPanelLg}
          />
        </div>
      </div>
    </div>
  );
}
